/**
 * @fileoverview Arbo Global Search - Main Entry Point
 *
 * This file serves as the main entry point for the Arbo Global Search micro-frontend component.
 * It provides a self-contained, reusable search interface that can be integrated into any
 * micro-frontend architecture or standalone application.
 *
 * ## Architecture Overview
 *
 * The project is designed as a modular micro-frontend component that can be:
 * - Bundled as a single JavaScript package
 * - Loaded dynamically into host applications
 * - Integrated with existing Vue.js applications
 * - Used as a standalone popup/modal component
 *
 * ## Bundle Structure
 *
 * When bundled, this project creates a single package containing:
 * - Vue.js application with all necessary components
 * - Pinia store for state management
 * - UI components from @arbocollab/arbo-ui-components
 * - Scoped CSS styles that won't conflict with host application
 * - Global search functionality with keyboard navigation
 *
 * ## Integration Methods
 *
 * ### 1. Direct Integration (Recommended)
 * ```javascript
 * import { createGlobalSearchInstance } from 'arbo-web-search';
 *
 * // Create and mount the search component
 * const searchApp = createGlobalSearchInstance();
 * searchApp.mount('#search-container');
 * ```
 *
 * ### 2. Window Global Method
 * ```javascript
 * // Available globally after script inclusion
 * const searchApp = window.createGlobalSearchInstance();
 * searchApp.mount('#search-container');
 * ```
 *
 * ### 3. Popup/Modal Integration
 * ```javascript
 * import { createGlobalSearchPopup } from 'arbo-web-search';
 *
 * // Creates a popup with backdrop overlay
 * const popup = createGlobalSearchPopup('search-popup');
 * const searchApp = createGlobalSearchInstance();
 * searchApp.mount(popup);
 * ```
 *
 * ## Configuration & Environment
 *
 * The component requires the following environment variables:
 * - `VITE_ARBO_BASE_URL`: Base domain for navigation links
 * - `VITE_ARBO_REVIEW_URL`: Review domain for scene navigation
 *
 * ## Features
 *
 * - **Multi-type Search**: People, Projects, Scenes, Recent accessed items
 * - **Keyboard Navigation**: Arrow keys, Enter, Escape, Ctrl+K shortcuts
 * - **Infinite Scroll**: Automatic loading of more results
 * - **Permission-based Actions**: Respects user permissions for navigation
 * - **Responsive Design**: Works on desktop and mobile devices
 * - **Accessibility**: ARIA compliant with keyboard navigation
 *
 * ## Development Mode
 *
 * In development mode, the component automatically:
 * - Mounts to a popup overlay
 * - Registers Ctrl+K keyboard shortcut for testing
 * - Provides hot reload capabilities
 *
 * ## Production Bundle
 *
 * The production bundle is optimized for:
 * - Tree shaking unused code
 * - CSS extraction and minification
 * - Code splitting for optimal loading
 * - TypeScript types for better integration
 *
 * @version 1.0.0
 * @author Arbo Team
 * @license MIT
 */

import 'tippy.js/dist/tippy.css';
import { initGlobalSearch } from './plugins';

/**
 * Creates a popup container for the global search component with backdrop overlay
 *
 * @param id - Unique identifier for the popup container
 * @returns HTMLDivElement - The created popup container element
 *
 * @example
 * ```typescript
 * const popup = createGlobalSearchPopup('my-search-popup');
 * const searchApp = createGlobalSearchInstance();
 * searchApp.mount(popup);
 * ```
 */
export const createGlobalSearchPopup = (id: string) => {
    /**
     * Injects CSS styles for the global search popup into the document head
     * These styles are scoped to avoid conflicts with the host application
     */
    const injectGlobalSearchStyles = () => {
        const style = document.createElement('style');
        style.textContent = `
        .arbo-global-search {
            display: none;
            position: fixed;
            top: 128px;
            left: calc(50vw - 500px);
            max-width: 1000px;
            width: 1000px;
            max-height: 80vh;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 888;
        }
        .arbo-global-search.show {
            display: block;
        }
        .arbo-global-search:before {
            content: '';
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: -1;
            background-color: rgba(0, 0, 0, 0.5);
        }
    `;
        document.head.appendChild(style);
    };
    const popup = document.createElement('div');
    popup.classList.add('arbo-global-search');
    popup.id = id;
    popup.setAttribute('el-id', 'popup-app');
    document.body.appendChild(popup);

    injectGlobalSearchStyles();
    return popup;
};

/**
 * Development mode setup - automatically creates and mounts the search component
 * This section only runs in development mode and provides:
 * - Automatic popup creation and mounting
 * - Ctrl+K keyboard shortcut for testing
 * - Hot reload capabilities
 */

// if (import.meta.env.DEV) {
//     sessionStorage.setItem('API_BASE_URL', 'https://api.dev.arbocollab.com/api/v1');
//     sessionStorage.setItem('ws.code', 'w:yngazem');
//     sessionStorage.setItem(
//         'ws.terminology',
//         JSON.stringify([
//             { code: 'project', name: 'Space' },
//             { code: 'process', name: 'Library' },
//             { code: 'episode', name: 'Folder2' },
//             { code: 'take', name: 'Folder' },
//             { code: 'scene', name: 'Task' }
//         ])
//     );
//     const id = 'arbo-global-search';
//     initGlobalSearch().then((appInstance) => {
//         let existingPopup = document.getElementById(id);
//         if (!existingPopup) {
//             existingPopup = createGlobalSearchPopup(id);
//         }
//         appInstance.config.globalProperties.rootElement = existingPopup;
//         appInstance.mount(existingPopup);
//         document.addEventListener('keydown', (event) => {
//             if (event.ctrlKey && event.key === 'k') {
//                 event.preventDefault();
//                 if (existingPopup) {
//                     existingPopup.classList.toggle('show');
//                 }
//             }
//         });
//     });
// }

/**
 * Export configurations for different integration methods:
 *
 * 1. window.createGlobalSearchInstance - Global method for script tag inclusion
 * 2. createGlobalSearchInstance - Named export for ES6 module imports
 * 3. default export - Default export for simplified imports
 *
 * These exports enable flexible integration across different bundling systems
 * and micro-frontend architectures.
 */
// Extend the Window interface to include createGlobalSearchInstance
declare global {
    interface Window {
        createGlobalSearchInstance: typeof initGlobalSearch;
    }
}


window.createGlobalSearchInstance = initGlobalSearch;
export const createGlobalSearchInstance = initGlobalSearch;
export default createGlobalSearchInstance;
